import { useClientPortalAPI } from "@certn/api";
import {
  BasicButton,
  Dialog,
  DialogContent,
  DialogHeader,
  Loader,
  Modal,
  Search,
  SectionMessage,
  Text,
} from "@certn/cascadia-react";
import { slugify } from "@certn/utils";
import { css } from "@emotion/css";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Bundle } from "../../components/bundle/Bundle";
import { BundleData } from "./types";

interface PackageEditAddBundleModalProps {
  selectedBundleIds: string[];
  onSelect: (bundle: BundleData) => void;
  onClose: () => void;
}

export function PackageEditAddBundleModal({
  selectedBundleIds,
  onSelect,
  onClose,
}: PackageEditAddBundleModalProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const {
    data: bundles = [],
    isLoading,
    error,
  } = useClientPortalAPI(
    `/api/internal/client-portal/customer-accounts/available-bundles/`
  );

  const matchingBundles = useMemo(() => {
    const selected = new Set(selectedBundleIds);
    const unselected = bundles.filter((bundle) => !selected.has(bundle.id));
    const searchTerm = search.trim().toLowerCase();

    if (!searchTerm) {
      return unselected;
    }

    return unselected.filter((bundle) =>
      bundle.title.toLowerCase().includes(searchTerm)
    );
  }, [bundles, search, selectedBundleIds]);

  return (
    <Modal
      isOpen
      testID="package-edit-add-bundle-modal"
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <Dialog>
        <DialogHeader onClose={onClose}>{t("Add bundled deal")}</DialogHeader>
        <DialogContent>
          <div className={styles.content}>
            <Search
              testID="package-edit-bundle-search"
              placeholder={t("Search bundled deals...")}
              value={search}
              onChange={setSearch}
              aria-label={t("Search bundled deals...")}
            />
            {error ? (
              <SectionMessage type="danger" icon="error">
                {String(error.message)}
              </SectionMessage>
            ) : isLoading ? (
              <Loader />
            ) : matchingBundles.length === 0 ? (
              <Text style="form/body-secondary">
                {search.trim()
                  ? t("No bundled deals match your search.")
                  : t(
                      "All available bundled deals are already on this package."
                    )}
              </Text>
            ) : (
              <div className={styles.cards}>
                {matchingBundles.map((bundle) => (
                  <Bundle
                    key={bundle.id}
                    bundleName={bundle.title}
                    checks={bundle.products.map((product) => product.name)}
                    price={bundle.total ? bundle.total.toString() : ""}
                    onPress={() => onSelect(bundle)}
                    isSelected={false}
                    testID={slugify(bundle.title)}
                    currency={bundle.currency}
                  />
                ))}
              </div>
            )}
            <BasicButton
              onPress={onClose}
              testID="package-edit-add-bundle-close"
            >
              {t("Close")}
            </BasicButton>
          </div>
        </DialogContent>
      </Dialog>
    </Modal>
  );
}

const styles = {
  content: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
    maxHeight: "70vh",
    overflowY: "auto",
  }),
  cards: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-3)",
  }),
};
